import React, { useState } from 'react';
import { Button } from './ui/button';
import { Check, Star, Droplets, X } from 'lucide-react';
import MultiStepForm from './MultiStepForm';

const LandingPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  const openForm = () => setIsFormOpen(true);
  const closeForm = () => setIsFormOpen(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="py-4" style={{ backgroundColor: '#FF003F' }}>
        <div className="container mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center">
            <Droplets className="w-8 h-8 text-white mr-2" />
            <h1 className="text-white text-2xl md:text-4xl font-bold">ProtegeFamilia</h1>
          </div>
          <Button 
            variant="outline" 
            className="bg-white text-black font-semibold px-3 md:px-6 py-2 md:py-3 text-sm md:text-base rounded-lg border-2 border-black hover:bg-gray-100 transition-colors hover-lift"
            onClick={openForm}
          >
            <span className="hidden sm:inline">SOLICITA UNA CONSULTA</span>
            <span className="sm:hidden">CONSULTA</span>
          </Button>
        </div>
      </header>

      {/* Limited Offer Banner */}
      <div className="bg-black text-white text-center py-3">
        <p className="font-semibold uppercase tracking-wide">
          <span className="border-b-2 border-white pb-1">OFERTA LIMITADA - TERMINA ESTE MES</span>
        </p>
      </div>

      {/* Hero Section */}
      <section className="relative text-white overflow-hidden" style={{ backgroundColor: '#18293F' }}>
        <div className="container mx-auto px-4 py-12 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8 fade-in">
              <div>
                <h2 className="text-3xl lg:text-5xl font-bold leading-tight mb-4">
                  Obtén un seguro de decesos desde solo
                </h2>
                <div className="flex items-center gap-2 mb-6">
                  <span className="text-5xl lg:text-6xl font-bold text-emerald-400">€3/mes</span>
                  <span className="text-2xl">👇</span>
                </div>
                <p className="text-xl text-gray-200 leading-relaxed">
                  Protege a tu familia de <em>miles de euros</em> en gastos funerarios.
                </p>
              </div>

              {/* Benefits */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="bg-white rounded-full p-1">
                    <Check className="w-5 h-5 text-slate-800" />
                  </div>
                  <span className="text-lg">Cubre más de €10,000 en gastos funerarios</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-white rounded-full p-1">
                    <Check className="w-5 h-5 text-slate-800" />
                  </div>
                  <span className="text-lg">Obtén tu póliza desde €3/mes</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-white rounded-full p-1">
                    <Check className="w-5 h-5 text-slate-800" />
                  </div>
                  <span className="text-lg">Calcula tu precio en 30 segundos</span>
                </div>
              </div>

              {/* CTA Button */}
              <div className="pt-4">
                <Button 
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 text-lg rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl pulse-glow hover-lift"
                  onClick={openForm}
                >
                  Encuentra pólizas en 30 segundos
                </Button>
                
                {/* Rating */}
                <div className="flex items-center gap-3 mt-6">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <span className="text-gray-200 text-sm">4.8/5 de 500,000 reseñas</span>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative hidden lg:block fade-in">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl hover-scale">
                <img 
                  src="https://agents.beunframed.com/wp-content/uploads/2025/08/image-36.webp"
                  alt="Protección y seguridad familiar"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials / Press Section */}
      <section className="bg-gray-50 py-12 lg:py-20 fade-in-up">
        <div className="container mx-auto px-4">
          {/* Press Logos */}
          <div className="text-center mb-12 fade-in-scale stagger-1">
            <div className="flex justify-center">
              <img 
                src="https://agents.beunframed.com/wp-content/uploads/2025/08/aconomist.png" 
                alt="Logos de prensa: elEconomista, ELMUNDO, LARAZÓN, ELPAÍS" 
                className="h-16 lg:h-20 hover-scale"
              />
            </div>
          </div>

          {/* Testimonial Card */}
          <div className="w-full fade-in-up stagger-2">
            <div className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 hover-lift">
              <div className="mb-6 fade-in-left stagger-3">
                <h3 className="text-lg font-bold text-gray-900 mb-2">María</h3>
                <p className="text-sm text-gray-600">16/06/24</p>
              </div>
              <blockquote className="text-base lg:text-lg text-gray-800 leading-relaxed fade-in-right stagger-4">
                "Mi madre lo hizo, y no tuvimos que hacer nada para pagar su funeral. Ya lo he hecho para mis hijas ❤️"
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* How to find the perfect policy Section */}
      <section className="bg-white py-12 lg:py-20 fade-in-up">
        <div className="container mx-auto px-4">
          {/* Title */}
          <div className="text-center mb-12 fade-in-scale stagger-1">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              ¿Cómo encontrar <span className="underline">la póliza perfecta?</span>
            </h2>
          </div>

          {/* Steps */}
          <div className="space-y-8 mb-12">
            {/* Step 1 */}
            <div className="bg-blue-50 rounded-2xl p-4 lg:p-6 flex items-center gap-4 lg:gap-6 fade-in-left stagger-2 hover-lift">
              <img 
                src="https://agents.beunframed.com/wp-content/uploads/2025/08/1.png" 
                alt="Solicitar documentos de información"
                className="w-16 h-16 lg:w-20 lg:h-20 flex-shrink-0 fade-in-scale stagger-3"
              />
              <div className="fade-in-right stagger-4">
                <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-2">1 – Solicita más información</h3>
                <p className="text-base text-gray-700">Haz clic en el botón y completa el formulario en menos de 1 minuto</p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-blue-50 rounded-2xl p-4 lg:p-6 flex items-center gap-4 lg:gap-6 fade-in-right stagger-3 hover-lift">
              <img 
                src="https://agents.beunframed.com/wp-content/uploads/2025/08/2.png" 
                alt="Consulta con experto"
                className="w-16 h-16 lg:w-20 lg:h-20 flex-shrink-0 fade-in-scale stagger-4"
              />
              <div className="fade-in-left stagger-5">
                <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-2">2 – Asesoramiento gratuito</h3>
                <p className="text-base text-gray-700">Un experto te ayudará a encontrar la mejor póliza disponible</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-blue-50 rounded-2xl p-4 lg:p-6 flex items-center gap-4 lg:gap-6 fade-in-left stagger-4 hover-lift">
              <img 
                src="https://agents.beunframed.com/wp-content/uploads/2025/08/3.png" 
                alt="Opciones de póliza flexibles"
                className="w-16 h-16 lg:w-20 lg:h-20 flex-shrink-0 fade-in-scale stagger-5"
              />
              <div className="fade-in-right stagger-6">
                <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-2">3 – Flexibilidad total</h3>
                <p className="text-base text-gray-700">Adapta la póliza a TUS necesidades – protege a tu familia a tu manera</p>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center mb-12 fade-in-scale stagger-5">
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-10 text-2xl lg:text-3xl rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl w-full pulse-glow hover-lift"
              onClick={openForm}
            >
              <div className="flex flex-col items-center gap-2 px-4 lg:px-0">
                <span className="text-lg md:text-2xl lg:text-3xl">Encuentra pólizas en 30 segundos</span>
                <span className="text-sm md:text-lg lg:text-xl font-normal opacity-90">Gratis y sin compromiso</span>
              </div>
            </Button>
          </div>

          {/* Statistics */}
          <div className="text-center fade-in-up stagger-6">
            <div className="flex items-center justify-center gap-4">
              <span className="text-4xl float">🤯</span>
              <p className="text-xl lg:text-2xl font-bold text-gray-900">
                Más del 50% de los españoles tienen <span className="text-blue-600">seguro de decesos</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="text-white py-12 lg:py-20" style={{ backgroundColor: '#18293F' }}>
        <div className="container mx-auto px-4">
          {/* Title */}
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              El seguro de decesos <span className="underline">marca la diferencia</span>
            </h2>
          </div>

          {/* Comparison Cards */}
          <div className="grid lg:grid-cols-2 gap-6 mb-12">
            {/* Without Insurance Card */}
            <div className="bg-gray-100 text-gray-900 rounded-2xl p-8">
              <div className="text-center mb-6">
                <div className="rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#FF003F' }}>
                  <X className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold">Sin seguro</h3>
              </div>
              
              <div className="space-y-4 text-center">
                <div className="bg-gray-200 rounded-lg p-6">
                  <div className="text-lg font-bold mb-2">€4,000+</div>
                  <div className="text-lg">en gastos inesperados</div>
                </div>
                
                <div className="bg-gray-300 rounded-lg p-6">
                  <div className="text-lg font-semibold">Papeleo</div>
                  <div className="text-lg font-semibold">complicado</div>
                </div>
                
                <div className="bg-gray-200 rounded-lg p-6">
                  <div className="text-lg font-semibold">Estrés y</div>
                  <div className="text-lg font-semibold">ansiedad</div>
                </div>
                
                <div className="bg-gray-300 rounded-lg p-6">
                  <div className="text-lg font-semibold">Proceso lento</div>
                  <div className="text-lg font-semibold">y difícil</div>
                </div>
              </div>
            </div>

            {/* With Insurance Card */}
            <div className="bg-gradient-to-b from-blue-500 to-blue-700 text-white rounded-2xl p-8">
              <div className="text-center mb-6">
                <div className="bg-green-500 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold">Con seguro</h3>
              </div>
              
              <div className="space-y-4">
                <div className="bg-blue-400 rounded-lg p-6 text-center">
                  <div className="text-lg font-bold">€3/mes –</div>
                  <div className="text-lg font-bold">cobertura total</div>
                </div>
                
                <div className="bg-blue-600 rounded-lg p-6 text-center">
                  <div className="text-lg font-bold">Gestión</div>
                  <div className="text-lg font-bold">profesional</div>
                </div>
                
                <div className="bg-blue-400 rounded-lg p-6 text-center">
                  <div className="text-lg font-bold">Tranquilidad</div>
                  <div className="text-lg font-bold">familiar</div>
                </div>
                
                <div className="bg-blue-600 rounded-lg p-6 text-center">
                  <div className="text-lg font-bold">Servicio inmediato</div>
                  <div className="text-lg font-bold">24/7</div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center mb-12">
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-10 text-2xl lg:text-3xl rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl w-full"
              onClick={openForm}
            >
              <div className="flex flex-col items-center gap-2 px-4 lg:px-0">
                <span className="text-lg md:text-2xl lg:text-3xl">Encuentra pólizas en 30 segundos</span>
                <span className="text-sm md:text-lg lg:text-xl font-normal opacity-90">Gratis y sin compromiso</span>
              </div>
            </Button>
          </div>

          {/* Bottom Text */}
          <div className="text-center">
            <p className="text-xl lg:text-2xl">
              Calcula el precio de tu póliza en menos de un minuto – GRATIS <span className="text-2xl">☝</span>
            </p>
          </div>
        </div>
      </section>

      {/* The best way to protect your family Section */}
      <section className="bg-gray-50 py-12 lg:py-20">
        <div className="container mx-auto px-4">
          {/* Title */}
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">
              La mejor manera de proteger a tu familia
            </h2>
          </div>

          {/* Benefits Grid */}
          <div className="grid lg:grid-cols-2 gap-12 mb-12 items-center">
            {/* Benefits List */}
            <div className="space-y-8">
              {/* Best price */}
              <div className="flex items-center gap-6 lg:gap-8">
                <div className="flex-shrink-0">
                  <span className="text-6xl lg:text-7xl">💰</span>
                </div>
                <div>
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">Mejor precio</h3>
                  <p className="text-lg text-gray-700">Cubre el 100% de tu funeral desde solo €3/mes</p>
                </div>
              </div>

              {/* All coverages */}
              <div className="flex items-center gap-6 lg:gap-8">
                <div className="flex-shrink-0">
                  <span className="text-6xl lg:text-7xl">📑</span>
                </div>
                <div>
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">Cobertura completa</h3>
                  <p className="text-lg text-gray-700">Incluye tanto los costos como los procedimientos administrativos</p>
                </div>
              </div>

              {/* Flexibility */}
              <div className="flex items-center gap-6 lg:gap-8">
                <div className="flex-shrink-0">
                  <span className="text-6xl lg:text-7xl">⚖</span>
                </div>
                <div>
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">Flexibilidad</h3>
                  <p className="text-lg text-gray-700">Adapta tu póliza a las necesidades de tu familia</p>
                </div>
              </div>

              {/* Find the best policy */}
              <div className="flex items-center gap-6 lg:gap-8">
                <div className="flex-shrink-0">
                  <span className="text-6xl lg:text-7xl">🔍</span>
                </div>
                <div>
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">Encuentra la mejor póliza</h3>
                  <p className="text-lg text-gray-700">Encuentra el plan perfecto para ti con nuestro formulario</p>
                </div>
              </div>
            </div>

            {/* Right Side Image - Desktop Only */}
            <div className="hidden lg:block">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                <img 
                  src="https://agents.beunframed.com/wp-content/uploads/2025/08/image-37.webp"
                  alt="Familia feliz protegida por seguro de vida"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-10 text-2xl lg:text-3xl rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl w-full"
              onClick={openForm}
            >
              <div className="flex flex-col items-center gap-2 px-4 lg:px-0">
                <span className="text-lg md:text-2xl lg:text-3xl">Encuentra pólizas en 30 segundos</span>
                <span className="text-sm md:text-lg lg:text-xl font-normal opacity-90">Gratis y sin compromiso</span>
              </div>
            </Button>
          </div>
        </div>
      </section>

      {/* Customer Testimonials Section */}
      <section className="bg-white py-12 lg:py-20">
        <div className="container mx-auto px-4">
          {/* Title */}
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">
              Más del 50% de los españoles ya lo tienen
            </h2>
          </div>

          {/* Testimonials Grid */}
          <div className="space-y-6 mb-12">
            {/* Álvaro Rioz */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-900">Álvaro Rioz</h3>
                <p className="text-sm text-gray-600">16/06/24</p>
              </div>
              <p className="text-base text-gray-800">
                "Muy contento con el trato y servicio. Te llaman y te explican la oferta detalladamente. Ya tenía seguro, pero ahora pago menos."
              </p>
            </div>

            {/* Juan */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-900">Juan</h3>
                <p className="text-sm text-gray-600">16/06/24</p>
              </div>
              <p className="text-base text-gray-800">
                "Me ayudaron mucho. Sandra, quien me atendió, fue muy amable."
              </p>
            </div>

            {/* Marta López */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-900">Marta López</h3>
                <p className="text-sm text-gray-600">16/06/24</p>
              </div>
              <p className="text-base text-gray-800">
                "Solicité información y me llamaron — muy amables — para explicarme la oferta que tenían. Me apunté. Gracias."
              </p>
            </div>

            {/* Julia Álvarez */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-900">Julia Álvarez</h3>
                <p className="text-sm text-gray-600">16/06/24</p>
              </div>
              <p className="text-base text-gray-800">
                "Vi un anuncio en Instagram, y sinceramente, la rapidez y profesionalidad fueron impresionantes. Contraté seguro de decesos por €5/mes para mí y mi marido."
              </p>
            </div>

            {/* Paco Romero */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-900">Paco Romero</h3>
                <p className="text-sm text-gray-600">16/06/24</p>
              </div>
              <p className="text-base text-gray-800">
                "Es molesto que te llamen, pero al final el asesoramiento se agradece. Ya me he apuntado. Solo espero no morirme pronto jajaja."
              </p>
            </div>

            {/* María */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-900">María</h3>
                <p className="text-sm text-gray-600">16/06/24</p>
              </div>
              <p className="text-base text-gray-800">
                "Mi madre lo hizo, y no tuvimos que hacer nada para pagar su funeral. Voy a hacerlo para mis hijas ❤️"
              </p>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-10 text-2xl lg:text-3xl rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl w-full"
              onClick={openForm}
            >
              <div className="flex flex-col items-center gap-2 px-4 lg:px-0">
                <span className="text-lg md:text-2xl lg:text-3xl">Encuentra pólizas en 30 segundos</span>
                <span className="text-sm md:text-lg lg:text-xl font-normal opacity-90">Gratis y sin compromiso</span>
              </div>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="bg-gray-100 py-12 lg:py-20">
        <div className="container mx-auto px-4">
          {/* Logo */}
          <div className="mb-8">
            <div className="flex items-center">
              <Droplets className="w-8 h-8 text-gray-600 mr-2" />
              <h3 className="text-3xl font-bold text-gray-800">ProtegeFamilia</h3>
            </div>
          </div>

          {/* Footer Links */}
          <div className="space-y-4 mb-12">
            <a href="#" className="block text-gray-600 hover:text-gray-800 transition-colors">
              Políticas de Privacidad
            </a>
            <a href="#" className="block text-gray-600 hover:text-gray-800 transition-colors">
              Términos y condiciones
            </a>
            <a href="#" className="block text-gray-600 hover:text-gray-800 transition-colors">
              Aviso legal
            </a>
            <a href="#" className="block text-gray-600 hover:text-gray-800 transition-colors">
              Política de Cookies
            </a>
          </div>

          {/* Legal Information */}
          <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
            <p>
              <span className="font-semibold">Responsable:</span> GRUPO MOUNOT S.L.
            </p>
            <p>
              <span className="font-semibold">Finalidad:</span> Gestión de la relación comercial y envío de ofertas personalizadas sobre productos o servicios en el sector energético y de call center.
            </p>
            <p>
              <span className="font-semibold">Base legal:</span> Consentimiento del interesado.
            </p>
            <p>
              <span className="font-semibold">Derechos:</span> Tienes derecho a acceder, rectificar y suprimir tus datos, así como otros derechos explicados en nuestra Política de Privacidad.
            </p>
          </div>
        </div>
      </footer>

      {/* Multi-Step Form Modal */}
      <MultiStepForm isOpen={isFormOpen} onClose={closeForm} />
    </div>
  );
};

export default LandingPage;